import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordField } from "@/components/PasswordField";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "@/lib/toast";
import { ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";
import { CloseAccountSection } from "@/components/CloseAccountSection";

export default function ChangePassword() {
  const { user, applyAuth } = useAuth();
  const hasPassword = user?.has_password !== false; // undefined => assume yes
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [pwValid, setPwValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const match = next.length > 0 && next === confirm;

  const onPwValidity = useCallback((v) => setPwValid(v), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid) return;
    if (!match) { setError("New passwords don't match."); return; }
    if (hasPassword && !current) { setError("Enter your current password."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: hasPassword ? current : undefined,
        new_password: next,
      });
      // Keep this session alive with the fresh token; other sessions are now dead.
      if (data.token) applyAuth(data.token, user);
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated. Other sessions signed out.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground" data-testid="change-password-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-olive-bright" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Password &amp; Security</h1>
        </div>
        <p className="text-sm text-secondary-foreground mt-1 font-body">
          {hasPassword
            ? "Change your password. Every other signed-in device will be logged out."
            : "Your account uses Google sign-in. Set a password so you can also log in with your email."}
        </p>

        <div className="mt-6 border border-sniper bg-surface p-5">
          {done && (
            <div data-testid="change-success" className="mb-4 border border-olive/40 bg-olive/10 text-olive-bright text-sm px-3 py-2.5 font-mono flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> Password updated. Other sessions have been signed out.
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            {error && <div data-testid="change-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{error}</div>}
            {hasPassword && (
              <div>
                <label htmlFor="cur-pw" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Current Password</label>
                <div className="mt-1">
                  <PasswordInput id="cur-pw" testId="change-current" inputTestId="change-current" toggleTestId="change-current-toggle"
                    value={current} onChange={setCurrent} autoComplete="current-password" />
                </div>
              </div>
            )}
            <PasswordField
              id="new-pw"
              testId="change-new"
              label={hasPassword ? "New Password" : "Password"}
              value={next}
              onChange={setNext}
              email={user?.email || ""}
              name={user?.name || ""}
              onValidityChange={onPwValidity}
            />
            <div>
              <label htmlFor="confirm-pw" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Confirm New Password</label>
              <div className="mt-1">
                <PasswordInput id="confirm-pw" testId="change-confirm" inputTestId="change-confirm" toggleTestId="change-confirm-toggle"
                  value={confirm} onChange={(v) => { setConfirm(v); if (!confirmTouched) setConfirmTouched(true); }} autoComplete="new-password" />
              </div>
              {confirmTouched && confirm.length > 0 && !match && (
                <p data-testid="change-confirm-mismatch" className="mt-1.5 text-xs font-mono text-blaze">Passwords don't match.</p>
              )}
              {match && (
                <p data-testid="change-confirm-match" className="mt-1.5 text-xs font-mono text-olive-bright">Passwords match.</p>
              )}
            </div>
            <button data-testid="change-submit" disabled={!pwValid || !match || busy || (hasPassword && !current)}
              className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-base py-3 transition-colors">
              {busy ? "Saving..." : hasPassword ? "Change Password" : "Set Password"}
            </button>
          </form>
        </div>
        <CloseAccountSection />
      </div>
    </div>
  );
}
